import type { ParseKeys, TFunction } from 'i18next'

import i18n, { DEFAULT_LANGUAGE, isLanguage, LOCALE_TAGS } from '@/i18n/config'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { describeProblem, fieldErrors } from '@/lib/problems'

/* A refused write, as the sentence the reader gets.
 *
 * `problems.ts` maps a problem document to KEYS and stops there, deliberately —
 * it is the transport layer's reading of the wire and has no business holding a
 * `t`. This is the layer above it: the one that turns those keys, plus the one
 * refusal that carries facts inside its field errors, into words.
 *
 * It lives in `lib/` because three features raise documents through the same
 * pipeline — the quote's offer, the case's invoice or debit note, the Money
 * page's client invoice — and every one of them can be handed the same FR-64
 * refusal. It used to live in `features/quotes/quoteText.ts`, which was true
 * while the offer sheet was the only surface that recognised it and became a
 * lie the moment a second one had to: a case document is not an offer, and a
 * feature module is not somewhere two other features should be importing their
 * error wording from.
 */

/* The FR-64 branding refusal, from the wire to a sentence.
 *
 * `platform/docs` reports it as one field — `issuingEntity` — carrying the bare
 * sentinel followed by one qualified code per missing field:
 * `[branding-incomplete, branding-incomplete:legal-footer,
 * branding-incomplete:bank-details]`. The sentinel leads and is what this
 * client matches on; the qualified codes are what it names. All three server
 * modules that can raise it — `internal/quote`, `internal/document`,
 * `internal/casefile` — answer that same shape through
 * `docs.ErrBrandingIncomplete.Codes`, so one reader here is enough.
 *
 * The field names arrive as MACHINE KEYS and the words stay here (AD-27). A
 * map rather than a sentence assembled server-side, because a server that
 * wrote «юридический футер» would be writing interface copy in a language it
 * does not know the reader is using — and because a key an older client cannot
 * word degrades to the unnamed sentence rather than to English inside Russian.
 *
 * `legal-footer` → `legalFooter` is the only transformation: the keys are
 * lower-case words joined by hyphens (docs.Branding.Missing pins that), and the
 * catalogue is camelCase like every other key in it. Written out rather than
 * computed so that a key with no words is a MISS here rather than a lookup of a
 * catalogue key that does not exist — the miss is what the drop below needs. */
const BRANDING_FIELD_KEYS: Record<string, ParseKeys> = {
  name: 'errors.branding.field.name',
  logo: 'errors.branding.field.logo',
  'legal-footer': 'errors.branding.field.legalFooter',
  'bank-details': 'errors.branding.field.bankDetails',
}

const BRANDING_CODE = 'branding-incomplete'

/** The fields a branding refusal named, or null when it is not that refusal.
 *
 * An empty array and null are different answers on purpose: the empty array is
 * "the entity's branding is incomplete and the server said no more than that",
 * which still belongs in Settings; null is "this was some other failure", which
 * must fall through to the generic mapping rather than acquire a cause the
 * client invented. */
export function brandingRefusalFields(error: unknown): string[] | null {
  const codes = fieldErrors(error)['issuingEntity']
  if (!codes?.includes(BRANDING_CODE)) return null
  return codes
    .filter((code) => code.startsWith(`${BRANDING_CODE}:`))
    .map((code) => code.slice(BRANDING_CODE.length + 1))
}

/** The branding refusal as one sentence, naming the fields and the screen.
 *
 * A field this catalogue has no words for is DROPPED rather than printed raw: a
 * server that grows a branding field before this client does would otherwise
 * show `vat-number` to a user. Dropping every one of them lands on the unnamed
 * sentence, which still says where to go — a worse message than the list, and a
 * far better one than a machine key in the middle of a Russian paragraph. */
export function brandingRefusalMessage(fields: string[], t: TFunction): string {
  const named = fields
    .map((field) => BRANDING_FIELD_KEYS[field])
    .filter((key): key is ParseKeys => key !== undefined)
    .map((key) => t(key))

  if (named.length === 0) return t('errors.branding.incomplete')
  return t('errors.branding.incompleteFields', {
    // Comma-joined here rather than in the catalogue: both languages punctuate
    // a list of nouns the same way, and a sentence holding the join could not
    // be given a different one per language anyway.
    fields: named.join(', '),
  })
}

/* The missing FX rate, from the wire to a sentence (S-4.1 follow-ups, item 3).
 *
 * The server refuses a write it cannot convert to USD with `rate-missing` (409)
 * and two extension members, `currency` and `rate_date` (`httpx.RateMissing`).
 * Which day is missing decides the remedy, so it decides the sentence:
 *
 *   · today or later — the hourly fill has not run yet, and waiting is the
 *     remedy (the provider only ever answers TODAY's rates);
 *   · a past day — nothing will ever fill it by itself, and an administrator
 *     enters it by hand.
 *
 * "Today" is the reader's calendar day. The server's may differ by a zone for
 * an hour around midnight; the worst that buys is the wrong one of two honest
 * sentences, never a wrong figure. */
const CURRENCY_CODE = /^[A-Z]{3}$/
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** The reader's calendar day as `YYYY-MM-DD`, in their own zone — not
 *  `toISOString()`, which is UTC's day: still yesterday in Ashgabat (UTC+5)
 *  until five in the morning. */
function isoDay(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** The `Intl` tag of the language `t` speaks.
 *
 *  A `t` from `useTranslation` or `getFixedT` carries the language it is bound
 *  to as `lng` (i18next sets it on every fixed `t`); a bare `i18n.t` does not,
 *  and speaks the instance's resolved language instead. Read here rather than
 *  taken as a parameter so the two `refusalMessage`s keep their `(error, t)`
 *  shape, and every caller of theirs gets the date in its reader's form. */
function localeOf(t: TFunction): string {
  const bound = (t as unknown as { lng?: unknown }).lng
  const language = typeof bound === 'string' ? bound.split('-')[0] : i18n.resolvedLanguage
  return LOCALE_TAGS[isLanguage(language) ? language : DEFAULT_LANGUAGE]
}

/** The `rate-missing` refusal as one sentence, or null when it is not that
 *  refusal.
 *
 *  Called FIRST by both `refusalMessage`s — this one and the case screen's
 *  (`features/cases/lib/refusal.ts`) — and by the quote conversion's sentence
 *  (`convertErrorMessage`) and `ErrorState`'s detail line, because the generic
 *  mapping can only say "there is no rate", and the reader needs to know
 *  whether to wait or to ask.
 *  A refusal without its members, or with one that is not a currency code or an
 *  ISO day, reads as the generic sentence: a value this client cannot vouch for
 *  is not printed. `now` is a parameter for the tests' sake. */
export function rateMissingMessage(
  error: unknown,
  t: TFunction,
  now: Date = new Date(),
): string | null {
  if (!(error instanceof ApiError) || error.code !== 'rate-missing') return null

  const { currency, rate_date: rateDate } = error.problem
  if (
    typeof currency !== 'string' ||
    !CURRENCY_CODE.test(currency) ||
    typeof rateDate !== 'string' ||
    !ISO_DAY.test(rateDate)
  ) {
    return t(describeProblem(error).detailKey)
  }

  // ISO days compare as strings: fixed width, most significant field first.
  if (rateDate >= isoDay(now)) {
    return t('errors.rateMissing.today', { currency })
  }
  return t('errors.rateMissing.past', {
    currency,
    date: formatDate(rateDate, localeOf(t)),
  })
}

/** A refused write as one line, in the viewer's language.
 *
 * The one call every sheet that raises or sends a document should make. Through
 * `describeProblem` rather than the problem's own title or detail (AD-22,
 * AD-27): those are written server-side in English for a log, and a Russian
 * user meeting a rejected save is the last person who should be handed a second
 * language.
 *
 * The branding case is checked FIRST because the generic mapping cannot word
 * it. It arrives as a validation problem on `issuingEntity` — a field none of
 * these sheets renders, so the field-level plumbing has nowhere to put it, and
 * "the highlighted fields could not be accepted" highlights nothing. The fix
 * lives in Settings → Entities, and only this sentence can send the reader
 * there. */
export function refusalMessage(error: unknown, t: TFunction): string {
  const rate = rateMissingMessage(error, t)
  if (rate !== null) return rate
  const missing = brandingRefusalFields(error)
  if (missing !== null) return brandingRefusalMessage(missing, t)
  return t(describeProblem(error).detailKey)
}
